'use client'

import { ReactNode } from 'react'

import { ItemPageSellerBadgesInfoDto } from '@marketplace-web/profile/profile-data'
import { clientSideMetrics } from '@marketplace-web/observability/client-metrics-util'
import {
  ItemPageSellerBadgesInfoPlugin,
  getItemPageSellerBadgesInfoPluginModel,
  ItemPageSellerBadgesInfoPluginModel,
} from '@marketplace-web/seller-engagement-tools/seller-engagement-tools-feature'
import {
  getItemPageAdminSectionPluginModel,
  ItemPageAdminSectionPlugin,
} from '@marketplace-web/admin-section/admin-section-feature'
import {
  ItemPageBreadcrumbsPluginModel,
  ItemPageSummaryDto,
  ItemPageSummaryPluginModel,
  ItemPageAttributesBlockPluginModel,
  ItemPageAttributesDto,
  ItemPageItemsBlockDto,
  ItemPageItemsBlockHeaderDto,
  ItemPageItemsBlockHeaderPluginModel,
  ItemPageItemsBlockPluginModel,
  ItemPageAlertDto,
  ItemPageAlertsPluginModel,
  ItemPageDescriptionPluginModel,
  ItemPageDescriptionDto,
  ItemPageInfoBannerModel,
  ItemPagePluginEntityDto,
  ItemPageAdminSectionDto,
  ItemPageAdminSectionPluginModel,
  PluginType,
  getItemPageItemsBlockPluginModel,
  getItemPageItemsBlockHeaderPluginModel,
  ItemPageHighDemandSignalInfoDto,
  ItemPageHighDemandSignalInfoPluginModel,
} from '@marketplace-web/item-page/item-page-data'
import {
  getItemPageItemAlertsPluginModel,
  ItemPageAlertsPlugin,
} from '@marketplace-web/alerts/alerts-feature'
import {
  ItemPageKlarnaPromoPlugin,
  getItemPageKlarnaPromoPluginModel,
} from '@marketplace-web/checkout-payments/checkout-payments-options-feature'
import {
  ItemPageKlarnaPromoDto,
  ItemPageKlarnaPromoPluginModel,
} from '@marketplace-web/checkout-payments/checkout-payments-options-data'

import { logItemPageError } from '../utils/observability'
import ItemPageAttributesBlockPlugin from './AttributesBlock/ItemPageAttributesBlockPlugin'
import { getItemPageAttributesBlockPluginModel } from './AttributesBlock/transformers'
import ItemPageSummaryPlugin from './Summary/ItemPageSummaryPlugin'
import { getItemPageSummaryPluginModel } from './Summary/transformers'
import ItemPageDescriptionPlugin from './Description/ItemPageDescriptionPlugin'
import { getItemPageDescriptionPluginModel } from './Description/transformers'
import ItemPageItemsBlockPlugin from './ItemsBlock/ItemPageItemsBlockPlugin'
import ItemPageItemsBlockHeaderPlugin from './ItemsBlock/ItemPageItemsBlockHeaderPlugin'
import ItemPageBreadcrumbsPlugin from './Breadcrumbs/ItemPageBreadcrumbsPlugin'
import { getItemPageBreadcrumbsPluginModel } from './Breadcrumbs/transformers'
import ItemPageInfoBannerPlugin from './InfoBanner/ItemPageInfoBannerPlugin'
import { getItemPageInfoBannerPluginModel } from './InfoBanner/transformers'
import ExposureBlock from '../components/ExposureBlock'
import { getItemPageHighDemandSignalInfoPluginModel } from './HighDemandSignalInfo/transformers'
import ItemPageHighDemandSignalInfoPlugin from './HighDemandSignalInfo/ItemPageHighDemandSignalInfoPlugin'

const ITEM_PAGE_PLUGIN_ERROR_COUNT_METRIC_NAME = 'item_page_plugin_error_count'

type TransformerParams<TBackendData> = {
  data: TBackendData
  pluginName: string
}

type PluginConfig<TBackendData, TProps> = {
  component: (props: TProps & { pluginName: string }) => ReactNode
  transformer: (params: TransformerParams<TBackendData>) => TProps | null
}

type PluginConfigs = {
  [PluginType.Attributes]: PluginConfig<ItemPageAttributesDto, ItemPageAttributesBlockPluginModel>
  [PluginType.Klarna]: PluginConfig<ItemPageKlarnaPromoDto, ItemPageKlarnaPromoPluginModel>
  [PluginType.Summary]: PluginConfig<ItemPageSummaryDto, ItemPageSummaryPluginModel>
  [PluginType.Description]: PluginConfig<ItemPageDescriptionDto, ItemPageDescriptionPluginModel>
  [PluginType.SellerBadgesInfo]: PluginConfig<
    ItemPageSellerBadgesInfoDto,
    ItemPageSellerBadgesInfoPluginModel
  >
  [PluginType.ItemsBlockHeader]: PluginConfig<
    ItemPageItemsBlockHeaderDto,
    ItemPageItemsBlockHeaderPluginModel
  >
  [PluginType.HorizontalItemsBlock]: PluginConfig<
    ItemPageItemsBlockDto,
    ItemPageItemsBlockPluginModel
  >
  [PluginType.VerticalItemsBlock]: PluginConfig<
    ItemPageItemsBlockDto,
    ItemPageItemsBlockPluginModel
  >
  [PluginType.AdminSection]: PluginConfig<ItemPageAdminSectionDto, ItemPageAdminSectionPluginModel>
  [PluginType.Alert]: PluginConfig<ItemPageAlertDto, ItemPageAlertsPluginModel>
  [PluginType.Breadcrumbs]: PluginConfig<
    ItemPageBreadcrumbsPluginModel,
    ItemPageBreadcrumbsPluginModel
  >
  [PluginType.InfoBanner]: PluginConfig<ItemPageInfoBannerModel, ItemPageInfoBannerModel>
  [PluginType.HighDemandSignalInfo]: PluginConfig<
    ItemPageHighDemandSignalInfoDto,
    ItemPageHighDemandSignalInfoPluginModel
  >
}

const withCatch = <TData, TProps>(
  { data, pluginName }: TransformerParams<TData>,
  transformPluginData: (data: TData) => TProps,
): TProps | null => {
  try {
    return transformPluginData(data)
  } catch (error) {
    logItemPageError(error, 'be_plugin_transformation_failed', `plugin: ${pluginName}`)

    return null
  }
}

const pluginConfigs: PluginConfigs = {
  [PluginType.Attributes]: {
    component: ItemPageAttributesBlockPlugin,
    transformer: pluginProps => withCatch(pluginProps, getItemPageAttributesBlockPluginModel),
  },
  [PluginType.Summary]: {
    component: ItemPageSummaryPlugin,
    transformer: pluginProps => withCatch(pluginProps, getItemPageSummaryPluginModel),
  },
  [PluginType.Klarna]: {
    component: ItemPageKlarnaPromoPlugin,
    transformer: pluginProps => withCatch(pluginProps, getItemPageKlarnaPromoPluginModel),
  },
  [PluginType.Description]: {
    component: ItemPageDescriptionPlugin,
    transformer: pluginProps => withCatch(pluginProps, getItemPageDescriptionPluginModel),
  },
  [PluginType.SellerBadgesInfo]: {
    component: ItemPageSellerBadgesInfoPlugin,
    transformer: pluginProps => withCatch(pluginProps, getItemPageSellerBadgesInfoPluginModel),
  },
  [PluginType.HorizontalItemsBlock]: {
    component: ItemPageItemsBlockPlugin,
    transformer: pluginProps =>
      withCatch(pluginProps, data => getItemPageItemsBlockPluginModel(data, true)),
  },
  [PluginType.VerticalItemsBlock]: {
    component: ItemPageItemsBlockPlugin,
    transformer: pluginProps =>
      withCatch(pluginProps, data => getItemPageItemsBlockPluginModel(data, false)),
  },
  [PluginType.ItemsBlockHeader]: {
    component: ItemPageItemsBlockHeaderPlugin,
    transformer: pluginProps => withCatch(pluginProps, getItemPageItemsBlockHeaderPluginModel),
  },
  [PluginType.AdminSection]: {
    component: ItemPageAdminSectionPlugin,
    transformer: pluginProps => withCatch(pluginProps, getItemPageAdminSectionPluginModel),
  },
  [PluginType.Alert]: {
    component: ItemPageAlertsPlugin,
    transformer: pluginProps => withCatch(pluginProps, getItemPageItemAlertsPluginModel),
  },
  [PluginType.Breadcrumbs]: {
    component: ItemPageBreadcrumbsPlugin,
    transformer: pluginProps => withCatch(pluginProps, getItemPageBreadcrumbsPluginModel),
  },
  [PluginType.InfoBanner]: {
    component: ItemPageInfoBannerPlugin,
    transformer: pluginProps => withCatch(pluginProps, getItemPageInfoBannerPluginModel),
  },
  [PluginType.HighDemandSignalInfo]: {
    component: ItemPageHighDemandSignalInfoPlugin,
    transformer: pluginProps => withCatch(pluginProps, getItemPageHighDemandSignalInfoPluginModel),
  },
}

type Props = {
  data: ItemPagePluginEntityDto | undefined
}

// /**
//  * @example
//  * const ExampleSidebarComponent = (props) => {
//  *    const relevantPlugins = plugins.filter(p => p.section == 'sidebar')
//  *    return <>
//  *      {relevantPlugins.map(p => <Plugin {...p} />)}
//  *    </>
//  * }
//  */
const Plugin = ({ data }: Props) => {
  if (!data) return undefined

  const pluginConfig: any = pluginConfigs[data.type]
  if (!pluginConfig) return undefined

  const componentProps = pluginConfig.transformer({ data: data.data, pluginName: data.name })
  if (!componentProps) {
    clientSideMetrics
      .counter(ITEM_PAGE_PLUGIN_ERROR_COUNT_METRIC_NAME, { name: data.name })
      .increment()
  }

  const Component = pluginConfig.component

  return (
    <>
      {data.exposure && <ExposureBlock {...data.exposure} />}
      {componentProps && <Component {...componentProps} pluginName={data.name} />}
    </>
  )
}

export default Plugin
